import { useQuery } from "@tanstack/react-query";
import { Navigate, useParams } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useSlug } from "@/hooks/useSlug";
import { useEventQueries } from "@/queries/events/queryOptions";
import { routes } from "@/routes";

import { type CreateOrEditEventFormValues, EventForm, type EventFormProps, type EventObserver } from "./EventForm";
import { getDefaultFormValues, useEditEvent } from "./Manager";

export function EditEventPage(): React.ReactNode {
  const sessionUser = useSessionUser();
  const slug = useSlug();
  const currentDate = useCurrentDate();
  const { id: eventId } = useParams<{ id: string }>();

  const { edit: editEvent, isSubmitting: isSubmittingEdit } = useEditEvent(eventId!);
  const eventQueries = useEventQueries();
  const {
    data: eventCategories = [],
    isFetching: eventCategoriesIsFetching,
    error: eventCategoriesError,
  } = useQuery(eventQueries.listCategories());

  const {
    data: eventDetails,
    isFetching: eventDetailsIsFetching,
    error: eventDetailsError,
  } = useQuery({
    ...eventQueries.details(
      {
        eventId: eventId!,
        query: { replaceParameters: false },
      },
      {
        translationMode: "original",
      },
    ),
    enabled: Boolean(eventId),
  });

  function onSubmit(formValues: CreateOrEditEventFormValues) {
    editEvent({
      ...formValues,
      audience: eventDetails?.canEditAudience ? formValues.audience : [],
    });
  }

  const error = eventDetailsError || eventCategoriesError;
  const isDataLoading = eventCategoriesIsFetching || eventDetailsIsFetching;

  if (error) {
    return <ErrorPage error={error} />;
  }

  // A list of projects or project connections that join this asset
  const eventObservers: EventObserver[] = [
    { type: "project", id: sessionUser.project.id, name: sessionUser.project.name },
    ...sessionUser.connections.map((x) => ({
      type: "projectConnection" as const,
      id: x.id,
      name: x.name,
    })),
  ];

  if (isDataLoading) {
    return <FullSizeLoader withPadding />;
  }

  if (eventDetails && (!eventDetails.canEdit || new Date(eventDetails.endsAt) <= currentDate)) {
    return <Navigate replace to={routes.events.details({ slug, id: eventId! })} />;
  }

  const formProps: EventFormProps = {
    contentLanguageId: eventDetails?.originalLanguageId,
    isEditMode: true,
    canEditAudience: eventDetails?.canEditAudience ?? false,
    isResident: !sessionUser.isAdmin,
    defaultFormValues: getDefaultFormValues(false, eventCategories, eventDetails, eventObservers),
    eventObservers,
    isSubmitting: isSubmittingEdit,
    onSubmit,
    showAnnouncementModal: false,
    eventCategories: sessionUser.isAdmin ? eventCategories : eventCategories.filter((x) => x.id !== "maintenance"),
    organizer: eventDetails?.organizer || sessionUser,
    isPublished: eventDetails?.publishedAt != null,
  };

  return <EventForm {...formProps} />;
}
